import {
  Smartphone, Zap, ShieldCheck, BarChart3, CreditCard, RefreshCw, Truck, Droplets,
  Coffee, Scissors, Hotel, ShoppingBag, Wrench, Utensils,
} from "lucide-react";
import gReviewHero from "@/assets/products/google-review-hero.webp";
import gReviewFeatures from "@/assets/products/google-review-features.webp";
import type { LucideIcon } from "lucide-react";

export const GR_SLUG = "google-review";
export const GR_BASE_PRICE = 30;
export const GR_HERO = gReviewHero;
export const GR_FEATURES_IMG = gReviewFeatures;

export type GRBundle = { qty: number; label: string; perCard: number; save?: number; badge?: string };
export type GRItem = { icon: LucideIcon; title?: string; desc?: string; label?: string };

export type GoogleReviewDict = {
  productUrl: string;
  routePath: string;
  locale?: string;
  productName: string;
  eyebrow: string;
  intro: string;
  ratingLabel: string;
  perCardSuffix: string;
  saveBadge: (pct: number) => string;
  seo: { title: string; description: string; ogTitle: string; ogDescription: string };
  jsonLdDescription: string;
  bundles: ReadonlyArray<GRBundle>;
  bundleSection: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    sub: string;
    perCardLabel: string;
    selected: string;
    choose: string;
  };
  usps: ReadonlyArray<{ icon: LucideIcon; label: string }>;
  features: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    items: ReadonlyArray<{ icon: LucideIcon; title: string; desc: string }>;
  };
  anatomy: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    imageAlt: string;
    items: ReadonlyArray<{ t: string; d: string }>;
  };
  industries: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    sub: string;
    items: ReadonlyArray<{ icon: LucideIcon; label: string }>;
  };
  stats: ReadonlyArray<{ stat: string; label: string }>;
  compare: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    sub: string;
    paperLabel: string;
    paper: ReadonlyArray<string>;
    product: ReadonlyArray<string>;
  };
  steps: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    sub: string;
    stepLabel: (n: number) => string;
    items: ReadonlyArray<{ title: string; desc: string }>;
  };
  faq: {
    eyebrow: string;
    headingPre: string; headingEm: string;
    items: ReadonlyArray<{ q: string; a: string }>;
  };
  outro: {
    headingPre: string; headingEm: string;
    body: string;
    primaryCta: string;
    secondaryCta: string;
  };
  buyBox: {
    addToCart: string;
    bundleEyebrow: string;
  };

  gallery: ReadonlyArray<{ src: string; alt: string }>;
};

export const googleReviewDict: GoogleReviewDict = {
  productUrl: "https://banana.cards/shop/google-review",
  routePath: "/shop/google-review",
  productName: "Google Review Cards",
  eyebrow: "banana.cards · Retail · Google Reviews",
  intro:
    "Turn happy customers into powerful social proof with Google Review Cards. A single tap or scan takes customers directly to your Google review page — no apps, no friction. Ideal for cafés, salons, hotels, retail stores and service businesses, these cards help boost visibility, trust and local SEO.",
  ratingLabel: "4.84 · 289 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  seo: {
    title: "Google Review Cards — Tap-to-Review NFC Cards · banana.cards",
    description: "Turn happy customers into 5-star Google reviews with a single tap. NFC + QR review cards built for cafés, salons, hotels, retail and service businesses.",
    ogTitle: "Google Review Cards — Tap-to-Review NFC Cards",
    ogDescription: "One tap or scan sends customers straight to your Google review page. No app, no friction. Built for local businesses.",
  },
  jsonLdDescription:
    "Tap-to-Review NFC cards designed to make leaving a Google review effortless. One tap or scan takes customers directly to your Google review page — no apps, no friction. Built for cafés, salons, hotels, retail and service businesses.",
  bundles: [
    { qty: 1,  label: "1 card",   perCard: 1.0 },
    { qty: 3,  label: "3 cards",  perCard: 0.9,  save: 10, badge: "Save 10%" },
    { qty: 5,  label: "5 cards",  perCard: 0.8,  save: 20, badge: "Most popular" },
    { qty: 10, label: "10 cards", perCard: 0.7,  save: 30, badge: "Best value" },
  ],
  bundleSection: {
    eyebrow: "Bundle & save",
    headingPre: "One per counter.",
    headingEm: "One per table.",
    sub: "Place a card at every touchpoint — checkout, table, reception — and watch your review velocity climb.",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Send customers to your reviews with one tap" },
    { icon: Zap,        label: "No app, no typing — Apple & Android friendly" },
    { icon: BarChart3,  label: "Boost local SEO with more 5-star reviews" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why Google Review Cards",
    headingPre: "Reviews on autopilot.",
    headingEm: "No app required.",
    items: [
      { icon: Smartphone, title: "Tap-to-review NFC",        desc: "Embedded NFC chip opens your Google review page on any modern smartphone — no app, no friction." },
      { icon: CreditCard, title: "Backup QR code",           desc: "Printed QR code on the back covers older devices and thick phone cases — every customer can leave a review." },
      { icon: Droplets,   title: "Built for the counter",    desc: "Durable, water-resistant white PVC — handles spills, sticky fingers and daily counter life." },
      { icon: BarChart3,  title: "Local SEO booster",        desc: "More recent positive reviews = higher rank in the Google Map Pack and local search." },
      { icon: RefreshCw,  title: "Update the link anytime",  desc: "Change your Google Business URL or move locations — your card keeps working. No reprinting." },
      { icon: Truck,      title: "Ships fast worldwide",     desc: "In stock and ready to ship. Free worldwide shipping on orders over $50." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    headingPre: "Engineered for the",
    headingEm: "five-star tap.",
    imageAlt: "Anatomy of the Google Review NFC card — NFC chip, durable white PVC and printed QR",
    items: [
      { t: "Embedded NFC chip",        d: "Tap to open your Google review page on any modern iPhone or Android — no app required." },
      { t: "Google review design",     d: "Recognisable, on-brand front so customers instantly know what to do — and why to do it." },
      { t: "White durable PVC material", d: "Water-resistant, scratch-resistant and built for daily counter, table or reception use." },
      { t: "Printed QR code",          d: "Universal fallback for older devices or thicker phone cases — every customer can leave a review." },
    ],
  },
  industries: {
    eyebrow: "Built for local businesses",
    headingPre: "Where the card",
    headingEm: "earns its stars.",
    sub: "Wherever you handle happy customers, the Google Review Card belongs.",
    items: [
      { icon: Coffee,     label: "Cafés & coffee shops" },
      { icon: Scissors,   label: "Salons & barbers" },
      { icon: Hotel,      label: "Hotels & rentals" },
      { icon: ShoppingBag,label: "Retail stores" },
      { icon: Wrench,     label: "Service businesses" },
      { icon: Utensils,   label: "Restaurants & bars" },
    ],
  },
  stats: [
    { stat: "3.2×", label: "More Google reviews per month on average" },
    { stat: "60s",  label: "Average setup time from unboxing to first tap" },
    { stat: "0",    label: "Apps, logins or training needed for customers" },
  ],
  compare: {
    eyebrow: "Asking vs tapping",
    headingPre: "Asking gets forgotten.",
    headingEm: "Tapping gets reviews.",
    sub: "Hope is not a review strategy. One tap is.",
    paperLabel: "Asking customers manually",
    paper: [
      "Hope customers remember to leave a review later",
      "Manually ask, beg and follow up over email",
      "Lose review momentum the moment they leave",
      "No analytics, no way to know what's working",
      "Look like everyone else on the high street",
    ],
    product: [
      "One tap takes them straight to your reviews",
      "Automatic, frictionless — customers actually do it",
      "Capture the moment while they're still happy",
      "Track taps, scans and conversions in real time",
      "Modern, premium, on-brand experience",
    ],
  },
  steps: {
    eyebrow: "How it works",
    headingPre: "You don't need to ask.",
    headingEm: "They just tap.",
    sub: "From happy customer to public 5-star review in under a minute.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Customer pays — you ask for a review", desc: "Place the card on your counter, checkout, table or hand it to your customer right after a great experience." },
      { title: "They tap or scan — no app needed",     desc: "Their phone opens your Google review page instantly. No typing, no searching, no friction to drop off." },
      { title: "Five stars land on your profile",      desc: "More fresh positive reviews boost trust, conversion and your ranking in local Google searches." },
    ],
  },
  faq: {
    eyebrow: "FAQ",
    headingPre: "Frequently asked",
    headingEm: "questions.",
    items: [
      { q: "How does the Google Review Card help my local SEO?", a: "Google's search algorithm prioritises businesses with a high volume of recent, positive reviews. By making the review process frictionless, the Google Review Card increases your review frequency — which directly improves your ranking in local search results and the Google Map Pack." },
      { q: "Do customers need to download an app to leave a review?", a: "No. A single tap on the NFC chip — or a scan of the printed QR code — opens your Google review page directly in their phone's default browser. No apps, no logins, no friction. It works on both iPhone and Android out of the box." },
      { q: "Can I use the same card for multiple business locations?", a: "Each card is linked to one Google Business Profile, so we recommend ordering one card per location to make sure each branch collects reviews for itself. You can manage every card from the same banana.cards dashboard and swap their destination link anytime." },
      { q: "Is the Google Review Card durable enough for a checkout counter?", a: "Yes. It's printed on a high-grade, water-resistant white PVC core — the same material used for premium membership cards. It handles daily counter life, sticky fingers, splashes and constant tapping without showing wear." },
      { q: "How do I set up my card to point to my review page?", a: "After checkout we send you a short setup link. You paste your Google Business Profile URL (or just search for your business) and we generate your unique short review link — pre-programmed onto the NFC chip and printed as a QR code. Setup takes about 60 seconds." },
      { q: "Does the card work on both iPhone and Android?", a: "Yes. Every modern iPhone (XS and newer) and every NFC-enabled Android reads the card with a simple tap. For older devices or thicker phone cases, the printed QR code is a perfect fallback." },
      { q: "What happens if I change my business name or Google link?", a: "Just log into your banana.cards dashboard and update the destination URL — every card in the field instantly points to the new link. You never have to reprint or replace your cards." },
      { q: "Can the card track how many people have scanned it?", a: "Yes. Your dashboard shows real-time taps and QR scans per card, per location and per day — so you can see exactly which counters, tables or staff members are driving the most reviews." },
      { q: "Is there a monthly fee to keep the review link active?", a: "No. The card includes a free banana.cards plan that keeps your review link active forever. You only need to upgrade if you want advanced analytics, multi-location dashboards or team management." },
      { q: "Why is a physical tap card better than just a QR code on a sticker?", a: "Tap is faster, more modern and feels premium — customers don't have to open a camera app, frame the code and tap a banner. Plus the card includes the QR as a backup, so you get both worlds: the speed of NFC and the universal compatibility of QR." },
    ],
  },
  outro: {
    headingPre: "Turn every visit into",
    headingEm: "five public stars.",
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my Google Review Cards",
    secondaryCta: "See all products",
  },
  buyBox: {
    addToCart: "Add to cart",
    bundleEyebrow: "Bundle & save",
  },

  gallery: [
    { src: gReviewHero,     alt: "Google Review NFC cards — front and back with QR code and tap icon" },
    { src: gReviewFeatures, alt: "Google Review card features — embedded NFC chip, durable white PVC, printed QR code" },
  ],
};

export const googleReviewDictVi: GoogleReviewDict = {
  ...googleReviewDict,
  routePath: "/vi/shop/google-review",
  productUrl: "https://banana.cards/vi/shop/google-review",
  locale: "vi_VN",
  productName: "Thẻ Đánh Giá Google",
  eyebrow: "banana.cards · Bán lẻ · Đánh giá Google",
  intro:
    "Biến khách hàng hài lòng thành bằng chứng xã hội mạnh mẽ với Thẻ Đánh Giá Google. Chỉ một cú chạm hoặc quét đưa khách đến thẳng trang đánh giá Google của bạn — không cần ứng dụng, không rào cản. Lý tưởng cho quán cà phê, salon, khách sạn, cửa hàng bán lẻ và dịch vụ.",
  ratingLabel: "4,84 · 289 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  seo: {
    title: "Thẻ Đánh Giá Google — Thẻ NFC Chạm-Để-Đánh-Giá · banana.cards",
    description: "Biến khách hàng hài lòng thành đánh giá 5 sao trên Google chỉ với một cú chạm. Thẻ NFC + QR cho quán cà phê, salon, khách sạn, bán lẻ và dịch vụ.",
    ogTitle: "Thẻ Đánh Giá Google — Thẻ NFC Chạm-Để-Đánh-Giá",
    ogDescription: "Một cú chạm hoặc quét đưa khách thẳng đến trang đánh giá Google. Không ứng dụng, không rào cản.",
  },
  jsonLdDescription:
    "Thẻ NFC Chạm-Để-Đánh-Giá giúp việc để lại đánh giá Google trở nên dễ dàng. Một cú chạm hoặc quét đưa khách đến trang đánh giá Google ngay lập tức — không ứng dụng, không rào cản.",
  bundles: [
    { qty: 1,  label: "1 thẻ",   perCard: 1.0 },
    { qty: 3,  label: "3 thẻ",   perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
    { qty: 5,  label: "5 thẻ",   perCard: 0.8,  save: 20, badge: "Phổ biến nhất" },
    { qty: 10, label: "10 thẻ",  perCard: 0.7,  save: 30, badge: "Giá trị nhất" },
  ],
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    headingPre: "Một thẻ mỗi quầy.",
    headingEm: "Một thẻ mỗi bàn.",
    sub: "Đặt thẻ ở mọi điểm chạm — quầy thanh toán, bàn, lễ tân — và xem tốc độ nhận đánh giá tăng vọt.",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Đưa khách đến trang đánh giá chỉ với một cú chạm" },
    { icon: Zap,        label: "Không ứng dụng, không gõ — hoạt động trên iPhone & Android" },
    { icon: BarChart3,  label: "Tăng SEO địa phương với nhiều đánh giá 5 sao hơn" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn Thẻ Đánh Giá Google",
    headingPre: "Đánh giá tự động.",
    headingEm: "Không cần ứng dụng.",
    items: [
      { icon: Smartphone, title: "NFC chạm-để-đánh-giá",     desc: "Chip NFC tích hợp mở trang đánh giá Google của bạn trên mọi smartphone hiện đại — không ứng dụng, không rào cản." },
      { icon: CreditCard, title: "Mã QR dự phòng",            desc: "Mã QR in ở mặt sau hoạt động cho thiết bị cũ và ốp lưng dày — mọi khách hàng đều có thể đánh giá." },
      { icon: Droplets,   title: "Bền cho quầy hàng",         desc: "PVC trắng bền, chống nước — chịu được nước đổ, tay dính và cuộc sống quầy hàng hàng ngày." },
      { icon: BarChart3,  title: "Tăng SEO địa phương",       desc: "Nhiều đánh giá tích cực gần đây = thứ hạng cao hơn trên Google Map Pack và tìm kiếm địa phương." },
      { icon: RefreshCw,  title: "Cập nhật liên kết bất cứ lúc nào", desc: "Thay đổi URL Google Business hoặc chuyển địa điểm — thẻ vẫn hoạt động. Không cần in lại." },
      { icon: Truck,      title: "Giao hàng toàn cầu",        desc: "Có hàng và sẵn sàng giao. Miễn phí vận chuyển toàn cầu cho đơn trên $50." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    headingPre: "Thiết kế cho",
    headingEm: "cú chạm năm sao.",
    imageAlt: "Cấu tạo Thẻ Đánh Giá Google NFC — chip NFC, PVC trắng bền và mã QR in sẵn",
    items: [
      { t: "Chip NFC tích hợp",        d: "Chạm để mở trang đánh giá Google trên mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Thiết kế đánh giá Google", d: "Mặt trước dễ nhận biết, đúng thương hiệu để khách hàng biết ngay phải làm gì." },
      { t: "Chất liệu PVC trắng bền",  d: "Chống nước, chống xước và bền cho việc sử dụng hàng ngày ở quầy, bàn hoặc lễ tân." },
      { t: "Mã QR in sẵn",             d: "Phương án dự phòng cho thiết bị cũ hoặc ốp lưng dày — mọi khách hàng đều có thể đánh giá." },
    ],
  },
  industries: {
    eyebrow: "Dành cho doanh nghiệp địa phương",
    headingPre: "Nơi thẻ",
    headingEm: "ghi điểm năm sao.",
    sub: "Bất cứ nơi nào bạn phục vụ khách hàng hài lòng, Thẻ Đánh Giá Google thuộc về nơi đó.",
    items: [
      { icon: Coffee,     label: "Quán cà phê" },
      { icon: Scissors,   label: "Salon & tiệm cắt tóc" },
      { icon: Hotel,      label: "Khách sạn & cho thuê" },
      { icon: ShoppingBag,label: "Cửa hàng bán lẻ" },
      { icon: Wrench,     label: "Doanh nghiệp dịch vụ" },
      { icon: Utensils,   label: "Nhà hàng & quán bar" },
    ],
  },
  stats: [
    { stat: "3,2×", label: "Đánh giá Google nhiều hơn mỗi tháng" },
    { stat: "60 giây", label: "Thời gian cài đặt trung bình từ mở hộp đến chạm đầu tiên" },
    { stat: "0",    label: "Ứng dụng, đăng nhập hay đào tạo cần thiết cho khách" },
  ],
  compare: {
    eyebrow: "Hỏi vs Chạm",
    headingPre: "Hỏi thì bị quên.",
    headingEm: "Chạm thì được đánh giá.",
    sub: "Hy vọng không phải là chiến lược. Một cú chạm thì có.",
    paperLabel: "Hỏi khách hàng thủ công",
    paper: [
      "Hy vọng khách nhớ để lại đánh giá sau",
      "Hỏi, năn nỉ và theo dõi thủ công qua email",
      "Mất đà ngay khi khách rời đi",
      "Không phân tích, không biết cái gì hiệu quả",
      "Trông giống mọi người khác trên phố",
    ],
    product: [
      "Một cú chạm đưa thẳng đến trang đánh giá",
      "Tự động, không rào cản — khách thực sự làm",
      "Bắt khoảnh khắc khi họ vẫn đang hài lòng",
      "Theo dõi chạm, quét và chuyển đổi thời gian thực",
      "Hiện đại, cao cấp, đúng thương hiệu",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    headingPre: "Bạn không cần hỏi.",
    headingEm: "Họ chỉ cần chạm.",
    sub: "Từ khách hàng hài lòng đến đánh giá 5 sao công khai trong dưới một phút.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Khách thanh toán — bạn xin đánh giá", desc: "Đặt thẻ trên quầy, bàn hoặc đưa cho khách ngay sau trải nghiệm tuyệt vời." },
      { title: "Họ chạm hoặc quét — không cần ứng dụng", desc: "Điện thoại mở trang đánh giá Google ngay lập tức. Không gõ, không tìm kiếm." },
      { title: "Năm sao xuất hiện trên hồ sơ",        desc: "Nhiều đánh giá tích cực mới tăng độ tin cậy, chuyển đổi và thứ hạng tìm kiếm địa phương." },
    ],
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    headingPre: "Câu hỏi",
    headingEm: "thường gặp.",
    items: [
      { q: "Thẻ Đánh Giá Google giúp SEO địa phương của tôi thế nào?", a: "Thuật toán Google ưu tiên doanh nghiệp có nhiều đánh giá tích cực gần đây. Bằng cách làm cho việc đánh giá không có rào cản, thẻ tăng tần suất đánh giá — cải thiện trực tiếp thứ hạng tìm kiếm địa phương và Google Map Pack." },
      { q: "Khách hàng có cần tải ứng dụng để đánh giá không?", a: "Không. Một cú chạm vào chip NFC — hoặc quét mã QR in — mở trang đánh giá Google trực tiếp trên trình duyệt mặc định. Không ứng dụng, không đăng nhập. Hoạt động trên iPhone và Android." },
      { q: "Tôi có thể dùng cùng một thẻ cho nhiều địa điểm không?", a: "Mỗi thẻ liên kết với một Hồ Sơ Doanh Nghiệp Google, nên chúng tôi khuyến nghị đặt một thẻ cho mỗi địa điểm. Bạn có thể quản lý mọi thẻ từ cùng bảng điều khiển banana.cards." },
      { q: "Thẻ có đủ bền cho quầy thanh toán không?", a: "Có. Thẻ in trên lõi PVC trắng cao cấp, chống nước — vật liệu tương tự thẻ thành viên cao cấp. Chịu được cuộc sống quầy hàng hàng ngày, tay dính, nước bắn và chạm liên tục." },
      { q: "Tôi cài đặt thẻ trỏ đến trang đánh giá thế nào?", a: "Sau khi thanh toán, chúng tôi gửi liên kết cài đặt ngắn. Bạn dán URL Hồ Sơ Doanh Nghiệp Google và chúng tôi tạo liên kết đánh giá ngắn duy nhất — lập trình sẵn vào chip NFC và in dưới dạng QR. Mất khoảng 60 giây." },
      { q: "Thẻ có hoạt động trên cả iPhone và Android không?", a: "Có. Mọi iPhone hiện đại (XS trở lên) và mọi Android có NFC đều đọc thẻ chỉ với một cú chạm. Mã QR in là phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp dày." },
      { q: "Điều gì xảy ra nếu tôi đổi tên doanh nghiệp hoặc liên kết Google?", a: "Chỉ cần đăng nhập bảng điều khiển banana.cards và cập nhật URL đích — mọi thẻ ngoài thị trường lập tức trỏ đến liên kết mới. Không cần in lại." },
      { q: "Thẻ có thể theo dõi bao nhiêu người đã quét không?", a: "Có. Bảng điều khiển hiển thị số lần chạm và quét QR theo thẻ, theo địa điểm và theo ngày — bạn biết chính xác quầy, bàn hoặc nhân viên nào đang mang lại nhiều đánh giá nhất." },
      { q: "Có phí hàng tháng để giữ liên kết đánh giá hoạt động không?", a: "Không. Thẻ đi kèm gói banana.cards miễn phí giữ liên kết hoạt động vĩnh viễn. Bạn chỉ cần nâng cấp nếu muốn phân tích nâng cao, bảng điều khiển đa địa điểm hoặc quản lý đội nhóm." },
      { q: "Tại sao thẻ chạm vật lý tốt hơn chỉ một sticker QR?", a: "Chạm nhanh hơn, hiện đại hơn và cao cấp hơn — khách không phải mở camera, căn khung. Thêm nữa, thẻ bao gồm QR làm dự phòng, nên bạn có cả hai: tốc độ NFC và tính tương thích phổ quát của QR." },
    ],
  },
  outro: {
    headingPre: "Biến mỗi lần ghé thăm thành",
    headingEm: "năm sao công khai.",
    body: "Đặt trước 3 giờ chiều, chúng tôi giao vào ngày làm việc tiếp theo. Miễn phí vận chuyển toàn cầu trên $50.",
    primaryCta: "Mua Thẻ Đánh Giá Google",
    secondaryCta: "Xem tất cả sản phẩm",
  },
  buyBox: {
    addToCart: "Thêm vào giỏ",
    bundleEyebrow: "Mua combo, tiết kiệm",
  },

  gallery: [
    { src: gReviewHero,     alt: "Thẻ NFC Đánh Giá Google — mặt trước và sau với mã QR và biểu tượng chạm" },
    { src: gReviewFeatures, alt: "Tính năng Thẻ Đánh Giá Google — chip NFC tích hợp, PVC trắng bền, mã QR in sẵn" },
  ],
};

const GR_PRODUCT_URL_EL = "https://banana.cards/el/shop/google-review";
const GR_BUNDLES_EL = [
  { qty: 1, label: "1 κάρτα", perCard: 1.0 },
  { qty: 3, label: "3 κάρτες", perCard: 0.9, save: 10, badge: "Κέρδος 10%" },
  { qty: 5, label: "5 κάρτες", perCard: 0.8, save: 20, badge: "Δημοφιλέστερο" },
  { qty: 10, label: "10 κάρτες", perCard: 0.7, save: 30, badge: "Καλύτερη τιμή" },
] as const;
const GR_GALLERY_EL = [
  { src: gReviewHero, alt: "Κάρτες αξιολογήσεων Google NFC — εμπρός και πίσω όψη με κωδικό QR και εικονίδιο αγγίγματος" },
  { src: gReviewFeatures, alt: "Χαρακτηριστικά κάρτας αξιολογήσεων Google — ενσωματωμένο τσιπ NFC, ανθεκτικό λευκό PVC και τυπωμένος κωδικός QR" },
];
export const googleReviewDictEl: GoogleReviewDict = {
  ...googleReviewDict,
  routePath: "/el/shop/google-review",
  productUrl: GR_PRODUCT_URL_EL,
  locale: "el_GR",
  productName: "Κάρτες αξιολογήσεων Google",
  eyebrow: "banana.cards · Λιανική · Αξιολογήσεις Google",
  intro: "Μετατρέψτε την ικανοποίηση των πελατών σας σε ισχυρή απόδειξη αξιοπιστίας με τις κάρτες αξιολογήσεων Google. Ένα άγγιγμα ή μία σάρωση οδηγεί τους πελάτες απευθείας στη σελίδα αξιολογήσεών σας στο Google — χωρίς εφαρμογές, χωρίς εμπόδια. Ιδανικές για καφέ, κομμωτήρια, ξενοδοχεία, καταστήματα και επιχειρήσεις υπηρεσιών, ενισχύουν την προβολή, την εμπιστοσύνη και το τοπικό SEO σας.",
  ratingLabel: "4,84 · 289 αξιολογήσεις",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  seo: {
    title: "Κάρτες NFC για αξιολογήσεις Google — banana.cards",
    description: "Κερδίστε αξιολογήσεις Google 5 αστέρων με ένα άγγιγμα. Κάρτες NFC + QR για καφέ, κομμωτήρια, ξενοδοχεία, καταστήματα και επιχειρήσεις υπηρεσιών.",
    ogTitle: "Κάρτες NFC για αξιολογήσεις Google με ένα άγγιγμα",
    ogDescription: "Ένα άγγιγμα ή μία σάρωση οδηγεί τους πελάτες στη σελίδα αξιολογήσεών σας στο Google. Χωρίς εφαρμογή ή εμπόδια. Για τοπικές επιχειρήσεις.",
  },
  jsonLdDescription: "Κάρτες NFC που κάνουν τις αξιολογήσεις Google πιο εύκολες από ποτέ. Ένα άγγιγμα ή μία σάρωση οδηγεί τους πελάτες απευθείας στη σελίδα αξιολογήσεών σας στο Google — χωρίς εφαρμογές, χωρίς εμπόδια. Για καφέ, κομμωτήρια, ξενοδοχεία, καταστήματα και επιχειρήσεις υπηρεσιών.",
  bundles: GR_BUNDLES_EL,
  bundleSection: {
    eyebrow: "Περισσότερες κάρτες, μεγαλύτερο όφελος", headingPre: "Μία σε κάθε πάγκο.", headingEm: "Μία σε κάθε τραπέζι.",
    sub: "Τοποθετήστε μια κάρτα σε κάθε σημείο εξυπηρέτησης — ταμείο, τραπέζι, υποδοχή — και δείτε τις αξιολογήσεις να πληθαίνουν.",
    perCardLabel: "ανά κάρτα", selected: "Επιλεγμένο", choose: "Επιλέξτε",
  },
  usps: [
    { icon: Smartphone, label: "Οδηγήστε τους πελάτες στις αξιολογήσεις σας με ένα άγγιγμα" },
    { icon: Zap, label: "Χωρίς εφαρμογή ή πληκτρολόγηση — για Apple και Android" },
    { icon: BarChart3, label: "Ενισχύστε το τοπικό SEO με περισσότερες αξιολογήσεις 5 αστέρων" },
    { icon: ShieldCheck, label: "Εγγύηση εφ’ όρου ζωής για την κάρτα" },
  ],
  features: {
    eyebrow: "Γιατί κάρτες αξιολογήσεων Google", headingPre: "Αξιολογήσεις στον αυτόματο.", headingEm: "Χωρίς εφαρμογή.",
    items: [
      { icon: Smartphone, title: "Αξιολόγηση με ένα άγγιγμα NFC", desc: "Το ενσωματωμένο τσιπ NFC ανοίγει τη σελίδα αξιολογήσεών σας στο Google σε κάθε σύγχρονο smartphone — χωρίς εφαρμογή, χωρίς εμπόδια." },
      { icon: CreditCard, title: "Εναλλακτικά, κωδικός QR", desc: "Ο τυπωμένος κωδικός QR στην πίσω όψη καλύπτει παλαιότερες συσκευές και χοντρές θήκες — κάθε πελάτης μπορεί να αφήσει αξιολόγηση." },
      { icon: Droplets, title: "Φτιαγμένη για τον πάγκο σας", desc: "Ανθεκτικό λευκό PVC με αντοχή στο νερό — αντέχει σε χυμένα υγρά, κολλώδη δάχτυλα και την καθημερινότητα του πάγκου." },
      { icon: BarChart3, title: "Ώθηση στο τοπικό SEO", desc: "Περισσότερες πρόσφατες θετικές αξιολογήσεις σημαίνουν υψηλότερη κατάταξη στα αποτελέσματα χαρτών του Google και στην τοπική αναζήτηση." },
      { icon: RefreshCw, title: "Αλλάξτε τον σύνδεσμο όποτε θέλετε", desc: "Αλλάξτε το URL του επαγγελματικού προφίλ σας στο Google ή μεταφερθείτε σε νέα διεύθυνση — η κάρτα συνεχίζει να λειτουργεί. Χωρίς επανεκτύπωση." },
      { icon: Truck, title: "Γρήγορη αποστολή παγκοσμίως", desc: "Άμεσα διαθέσιμη και έτοιμη για αποστολή. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50." },
    ],
  },
  anatomy: {
    eyebrow: "Η κάρτα από κοντά", headingPre: "Σχεδιασμένη για", headingEm: "ένα άγγιγμα πέντε αστέρων.",
    imageAlt: "Ανατομία κάρτας αξιολογήσεων Google NFC — τσιπ NFC, ανθεκτικό λευκό PVC και τυπωμένος QR",
    items: [
      { t: "Ενσωματωμένο τσιπ NFC", d: "Αγγίζετε την κάρτα σε κάθε σύγχρονο iPhone ή Android και ανοίγει η σελίδα αξιολογήσεών σας στο Google — χωρίς εφαρμογή." },
      { t: "Σχεδιασμός για αξιολογήσεις Google", d: "Αναγνωρίσιμη εμπρός όψη, με τη σωστή εταιρική ταυτότητα, ώστε οι πελάτες να ξέρουν αμέσως τι να κάνουν — και γιατί." },
      { t: "Ανθεκτικό λευκό PVC", d: "Αντέχει στο νερό και στις γρατζουνιές, για καθημερινή χρήση στον πάγκο, στο τραπέζι ή στην υποδοχή." },
      { t: "Τυπωμένος κωδικός QR", d: "Εναλλακτική ευρείας συμβατότητας για παλαιότερες συσκευές ή πιο χοντρές θήκες — κάθε πελάτης μπορεί να αφήσει αξιολόγηση." },
    ],
  },
  industries: {
    eyebrow: "Για τοπικές επιχειρήσεις", headingPre: "Εκεί όπου η κάρτα", headingEm: "κερδίζει τα αστέρια της.",
    sub: "Όπου εξυπηρετείτε ευχαριστημένους πελάτες, υπάρχει θέση για μια κάρτα αξιολογήσεων Google.",
    items: [
      { icon: Coffee, label: "Καφέ και καφετέριες" },
      { icon: Scissors, label: "Κομμωτήρια και κουρεία" },
      { icon: Hotel, label: "Ξενοδοχεία και ενοικιαζόμενα καταλύματα" },
      { icon: ShoppingBag, label: "Καταστήματα λιανικής" },
      { icon: Wrench, label: "Επιχειρήσεις υπηρεσιών" },
      { icon: Utensils, label: "Εστιατόρια και μπαρ" },
    ],
  },
  stats: [
    { stat: "3,2×", label: "Περισσότερες αξιολογήσεις Google τον μήνα κατά μέσο όρο" },
    { stat: "60 δευτ.", label: "Μέσος χρόνος ρύθμισης από το άνοιγμα της συσκευασίας έως το πρώτο άγγιγμα" },
    { stat: "0", label: "Εφαρμογές, συνδέσεις ή εκπαίδευση που χρειάζονται οι πελάτες" },
  ],
  compare: {
    eyebrow: "Παράκληση ή άγγιγμα", headingPre: "Η παράκληση ξεχνιέται.", headingEm: "Το άγγιγμα φέρνει αξιολογήσεις.",
    sub: "Η ελπίδα δεν είναι στρατηγική για αξιολογήσεις. Ένα άγγιγμα είναι.", paperLabel: "Ζητώντας αξιολογήσεις προσωπικά",
    paper: ["Ελπίζετε να θυμηθούν οι πελάτες να αφήσουν αξιολόγηση αργότερα", "Ζητάτε, παρακαλάτε και στέλνετε υπενθυμίσεις με email", "Χάνετε τη δυναμική μόλις φύγει ο πελάτης", "Χωρίς στατιστικά, δεν ξέρετε τι αποδίδει", "Δεν ξεχωρίζετε από τις άλλες επιχειρήσεις της περιοχής"],
    product: ["Ένα άγγιγμα τους οδηγεί απευθείας στις αξιολογήσεις σας", "Αυτόματα και χωρίς εμπόδια — οι πελάτες όντως αφήνουν αξιολόγηση", "Αξιοποιείτε τη στιγμή που είναι ακόμη ευχαριστημένοι", "Παρακολουθείτε αγγίγματα, σαρώσεις και μετατροπές σε πραγματικό χρόνο", "Σύγχρονη εμπειρία κορυφαίας ποιότητας, με την ταυτότητά σας"],
  },
  steps: {
    eyebrow: "Πώς λειτουργεί", headingPre: "Δεν χρειάζεται να ζητήσετε.", headingEm: "Αρκεί ένα άγγιγμα.",
    sub: "Από ευχαριστημένος πελάτης σε δημόσια αξιολόγηση 5 αστέρων, σε λιγότερο από ένα λεπτό.",
    stepLabel: (n) => `Βήμα ${n}`,
    items: [
      { title: "Ο πελάτης πληρώνει — ζητάτε μια αξιολόγηση", desc: "Τοποθετήστε την κάρτα στον πάγκο, στο ταμείο ή στο τραπέζι, ή δώστε τη στον πελάτη αμέσως μετά από μια εξαιρετική εμπειρία." },
      { title: "Ένα άγγιγμα ή μία σάρωση — χωρίς εφαρμογή", desc: "Το κινητό του ανοίγει αμέσως τη σελίδα αξιολογήσεών σας στο Google. Χωρίς πληκτρολόγηση, αναζήτηση ή εμπόδια που θα τον αποθαρρύνουν." },
      { title: "Πέντε αστέρια προστίθενται στο προφίλ σας", desc: "Περισσότερες πρόσφατες θετικές αξιολογήσεις ενισχύουν την εμπιστοσύνη, τις μετατροπές και την κατάταξή σας στις τοπικές αναζητήσεις Google." },
    ],
  },
  faq: {
    eyebrow: "Συχνές ερωτήσεις", headingPre: "Οι πιο συχνές", headingEm: "ερωτήσεις σας.",
    items: [
      { q: "Πώς βοηθά η κάρτα αξιολογήσεων Google το τοπικό SEO μου;", a: "Ο αλγόριθμος αναζήτησης του Google δίνει προτεραιότητα σε επιχειρήσεις με πολλές πρόσφατες θετικές αξιολογήσεις. Κάνοντας τη διαδικασία εύκολη, η κάρτα αξιολογήσεων Google αυξάνει τη συχνότητα των αξιολογήσεών σας — βελτιώνοντας άμεσα την κατάταξή σας στα τοπικά αποτελέσματα αναζήτησης και στα αποτελέσματα χαρτών του Google." },
      { q: "Χρειάζεται να κατεβάσουν εφαρμογή οι πελάτες για να αφήσουν αξιολόγηση;", a: "Όχι. Ένα άγγιγμα στο τσιπ NFC ή μία σάρωση του τυπωμένου κωδικού QR ανοίγει τη σελίδα αξιολογήσεών σας στο Google απευθείας στο προεπιλεγμένο πρόγραμμα περιήγησης του κινητού τους. Χωρίς εφαρμογές, συνδέσεις ή εμπόδια. Λειτουργεί αμέσως σε iPhone και Android." },
      { q: "Μπορώ να χρησιμοποιήσω την ίδια κάρτα για πολλά καταστήματα;", a: "Κάθε κάρτα συνδέεται με ένα επαγγελματικό προφίλ Google, γι’ αυτό προτείνουμε μία κάρτα ανά τοποθεσία, ώστε κάθε υποκατάστημα να συγκεντρώνει τις δικές του αξιολογήσεις. Μπορείτε να διαχειρίζεστε όλες τις κάρτες από τον ίδιο πίνακα ελέγχου banana.cards και να αλλάζετε τον σύνδεσμο προορισμού τους όποτε θέλετε." },
      { q: "Είναι αρκετά ανθεκτική η κάρτα για τον πάγκο του ταμείου;", a: "Ναι. Τυπώνεται σε πυρήνα από λευκό PVC υψηλής ποιότητας με αντοχή στο νερό — το ίδιο υλικό που χρησιμοποιείται σε κορυφαίες κάρτες μέλους. Αντέχει στην καθημερινή χρήση στον πάγκο, σε κολλώδη δάχτυλα, πιτσιλιές και συνεχή αγγίγματα, χωρίς εμφανή φθορά." },
      { q: "Πώς ρυθμίζω την κάρτα ώστε να ανοίγει τη σελίδα αξιολογήσεών μου;", a: "Μετά την αγορά, σας στέλνουμε έναν σύνδεσμο για σύντομη ρύθμιση. Επικολλάτε το URL του επαγγελματικού προφίλ σας στο Google ή απλώς αναζητάτε την επιχείρησή σας. Δημιουργούμε τον μοναδικό σύντομο σύνδεσμο αξιολογήσεών σας, προγραμματισμένο στο τσιπ NFC και τυπωμένο ως κωδικό QR. Η ρύθμιση διαρκεί περίπου 60 δευτερόλεπτα." },
      { q: "Λειτουργεί η κάρτα σε iPhone και Android;", a: "Ναι. Κάθε σύγχρονο iPhone, από XS και νεότερο, και κάθε Android με NFC διαβάζει την κάρτα με ένα απλό άγγιγμα. Για παλαιότερες συσκευές ή πιο χοντρές θήκες, ο τυπωμένος κωδικός QR είναι η ιδανική εναλλακτική." },
      { q: "Τι γίνεται αν αλλάξω την επωνυμία μου ή τον σύνδεσμο Google;", a: "Απλώς συνδεθείτε στον πίνακα ελέγχου banana.cards και ενημερώστε το URL προορισμού. Κάθε κάρτα που χρησιμοποιείται οδηγεί αμέσως στον νέο σύνδεσμο. Δεν χρειάζεται ποτέ να επανεκτυπώσετε ή να αντικαταστήσετε τις κάρτες σας." },
      { q: "Μπορώ να παρακολουθώ πόσοι σαρώνουν την κάρτα;", a: "Ναι. Ο πίνακας ελέγχου εμφανίζει αγγίγματα και σαρώσεις QR σε πραγματικό χρόνο, ανά κάρτα, τοποθεσία και ημέρα. Έτσι βλέπετε ακριβώς ποιοι πάγκοι, τραπέζια ή εργαζόμενοι φέρνουν τις περισσότερες αξιολογήσεις." },
      { q: "Υπάρχει μηνιαία χρέωση για να παραμένει ενεργός ο σύνδεσμος αξιολογήσεων;", a: "Όχι. Η κάρτα περιλαμβάνει δωρεάν πρόγραμμα banana.cards που διατηρεί τον σύνδεσμο αξιολογήσεών σας ενεργό για πάντα. Αναβάθμιση χρειάζεται μόνο αν θέλετε προηγμένα στατιστικά, πίνακες ελέγχου για πολλές τοποθεσίες ή διαχείριση ομάδας." },
      { q: "Γιατί είναι καλύτερη μια κάρτα NFC από έναν κωδικό QR σε αυτοκόλλητο;", a: "Το άγγιγμα είναι πιο γρήγορο, πιο σύγχρονο και προσφέρει αίσθηση κορυφαίας ποιότητας. Οι πελάτες δεν χρειάζεται να ανοίξουν την κάμερα, να κεντράρουν τον κωδικό και να επιλέξουν την ειδοποίηση. Επιπλέον, η κάρτα περιλαμβάνει QR ως εναλλακτική, συνδυάζοντας την ταχύτητα του NFC με την ευρεία συμβατότητα του QR." },
    ],
  },
  outro: {
    headingPre: "Μετατρέψτε κάθε επίσκεψη σε", headingEm: "πέντε αστέρια που βλέπουν όλοι.",
    body: "Παραγγείλετε πριν τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50.",
    primaryCta: "Αποκτήστε κάρτες αξιολογήσεων Google", secondaryCta: "Δείτε όλα τα προϊόντα",
  },
  buyBox: { addToCart: "Προσθήκη στο καλάθι", bundleEyebrow: "Πακέτα με όφελος" },
  gallery: GR_GALLERY_EL,
};
